/**
 * OnboardingSlideshow.jsx
 * ========================
 *
 * Player de slideshow SlideShare-style pour l'onboarding client.
 *  - Navigation clavier : ← / → / Espace / Home / End / Esc
 *  - Fullscreen (F ou bouton)
 *  - Progress bar en haut
 *  - Compteur slide + thumbnail strip
 *  - Animation slide cross-fade
 *
 * Usage :
 *   <OnboardingSlideshow slides={ONBOARDING_SLIDES} onClose={() => navigate(-1)} />
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, Maximize2, Minimize2, X, Grid3x3, Pause, Play, Download,
} from 'lucide-react';
import { Button } from '../ui/button';

const OnboardingSlideshow = ({
  slides = [],
  onClose,
  pdfDownloadUrl,
  startIndex = 0,
  onSlideChange,
}) => {
  const [current, setCurrent] = useState(startIndex);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showThumbs, setShowThumbs] = useState(false);
  const [autoplay, setAutoplay] = useState(false);
  const containerRef = useRef(null);
  const total = slides.length;

  // Notifier le parent du changement de slide (pour tracking progression)
  useEffect(() => {
    if (typeof onSlideChange === 'function') {
      onSlideChange(current, total);
    }
  }, [current, total, onSlideChange]);

  const goTo = useCallback(
    (idx) => {
      if (idx < 0) idx = 0;
      if (idx >= total) idx = total - 1;
      setCurrent(idx);
    },
    [total]
  );

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  // ----- Clavier -----
  useEffect(() => {
    const handler = (e) => {
      // Ignore typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          next();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          prev();
          break;
        case 'Home':
          e.preventDefault();
          goTo(0);
          break;
        case 'End':
          e.preventDefault();
          goTo(total - 1);
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'Escape':
          if (showThumbs) setShowThumbs(false);
          else if (document.fullscreenElement) document.exitFullscreen();
          else if (onClose) onClose();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, total, next, prev, goTo, showThumbs, onClose]);

  // ----- Fullscreen -----
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // ----- Autoplay (6s par slide) -----
  useEffect(() => {
    if (!autoplay) return undefined;
    const t = setTimeout(() => {
      if (current >= total - 1) setAutoplay(false);
      else next();
    }, 6000);
    return () => clearTimeout(t);
  }, [autoplay, current, total, next]);

  const Current = slides[current]?.Component;

  return (
    <div
      ref={containerRef}
      data-testid="onboarding-slideshow"
      className="fixed inset-0 z-[100] bg-black flex flex-col select-none"
    >
      {/* Progress bar fine en haut */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-black/50 z-30">
        <div
          className="h-full bg-[#FCD34D] transition-all duration-300"
          style={{ width: `${((current + 1) / total) * 100}%` }}
          data-testid="slideshow-progress"
        />
      </div>

      {/* Scène : aspect 16/9 centré */}
      <div className="flex-1 flex items-center justify-center p-2 md:p-6 overflow-hidden">
        <div
          className="relative w-full max-w-[1600px] bg-white shadow-2xl rounded-lg overflow-hidden"
          style={{ aspectRatio: '16 / 9' }}
          data-testid={`slide-stage-${current + 1}`}
        >
          {Current && (
            <div key={current} className="absolute inset-0 animate-fade-in">
              <Current />
            </div>
          )}

          {/* Click-zones invisible left/right for next/prev */}
          <button
            type="button"
            aria-label="Slide précédente"
            onClick={prev}
            disabled={current === 0}
            className="absolute left-0 top-0 bottom-16 w-1/4 cursor-pointer focus:outline-none disabled:cursor-default"
            data-testid="slideshow-prev-zone"
          />
          <button
            type="button"
            aria-label="Slide suivante"
            onClick={next}
            disabled={current === total - 1}
            className="absolute right-0 top-0 bottom-16 w-1/4 cursor-pointer focus:outline-none disabled:cursor-default"
            data-testid="slideshow-next-zone"
          />
        </div>
      </div>

      {/* Toolbar bas */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-md text-white px-4 md:px-8 py-3 flex items-center gap-3 z-20">
        <Button
          variant="ghost"
          size="sm"
          onClick={prev}
          disabled={current === 0}
          className="text-white hover:bg-white/15 disabled:opacity-30"
          data-testid="slideshow-prev-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>

        <div className="font-mono text-sm tabular-nums min-w-[60px] text-center" data-testid="slideshow-counter">
          {current + 1} / {total}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={next}
          disabled={current === total - 1}
          className="text-white hover:bg-white/15 disabled:opacity-30"
          data-testid="slideshow-next-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </Button>

        <div className="flex-1 truncate text-sm text-[#A7F3D0] hidden md:block">
          {slides[current]?.title}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setAutoplay((a) => !a)}
          className="text-white hover:bg-white/15"
          title={autoplay ? 'Pause autoplay' : 'Démarrer autoplay (6s/slide)'}
          data-testid="slideshow-autoplay-btn"
        >
          {autoplay ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowThumbs((s) => !s)}
          className="text-white hover:bg-white/15"
          title="Vue d'ensemble (toutes les slides)"
          data-testid="slideshow-grid-btn"
        >
          <Grid3x3 className="w-4 h-4" />
        </Button>

        {pdfDownloadUrl && (
          <a
            href={pdfDownloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-white hover:bg-white/15 text-sm"
            title="Télécharger en PDF"
            data-testid="slideshow-download-pdf-btn"
          >
            <Download className="w-4 h-4" />
            <span className="hidden md:inline">PDF</span>
          </a>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={toggleFullscreen}
          className="text-white hover:bg-white/15"
          title={isFullscreen ? 'Quitter plein écran (F)' : 'Plein écran (F)'}
          data-testid="slideshow-fullscreen-btn"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </Button>

        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white hover:bg-white/15"
            title="Fermer (Esc)"
            data-testid="slideshow-close-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Thumbnail grid overlay */}
      {showThumbs && (
        <div
          className="absolute inset-0 bg-black/95 z-30 overflow-auto p-6 md:p-10"
          data-testid="slideshow-thumb-grid"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-white text-xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>
              Toutes les slides
            </h3>
            <Button
              variant="ghost"
              onClick={() => setShowThumbs(false)}
              className="text-white hover:bg-white/15"
              data-testid="slideshow-thumb-close"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {slides.map((s, idx) => {
              const C = s.Component;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setCurrent(idx);
                    setShowThumbs(false);
                  }}
                  className={`relative rounded-lg overflow-hidden border-2 transition ${
                    idx === current ? 'border-[#FCD34D]' : 'border-white/15 hover:border-white/40'
                  }`}
                  data-testid={`slideshow-thumb-${idx + 1}`}
                  style={{ aspectRatio: '16 / 9' }}
                >
                  <div className="absolute inset-0 origin-top-left bg-white" style={{ transform: 'scale(0.18)', width: '555%', height: '555%' }}>
                    <C />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-white px-2 py-1 text-xs font-medium flex justify-between">
                    <span className="truncate">{s.title}</span>
                    <span className="font-mono">{idx + 1}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 320ms ease-out; }
      `}</style>
    </div>
  );
};

export default OnboardingSlideshow;
